import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, LocateFixed, MapPin, Search } from 'lucide-react';

import BottomNav from '@/components/custom/BottomNav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getLandingCardColors } from '@/data/recorridos/config';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { trackBuscarCercanos, trackVerLlegadas } from '@/lib/analytics';
import { geolocationDisponible, geolocationErrorMessage } from '@/lib/geolocation';
import { llegadasPath } from '@/lib/paraderoRoutes';
import {
  buscarParaderos,
  distanciaM,
  formatoDistancia,
  paraderosMasCercanos,
} from '@/lib/paraderosFisicos';

/** Más lejos que esto del paradero más cercano, la persona no está en la zona de los recorridos. */
const LEJOS_DE_LA_RED_M = 3000;

function ParaderoItem({ paradero, onSelect }) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(paradero)}
        className="flex w-full items-center gap-3 rounded-2xl border border-border/60 bg-card p-4 text-left shadow-card transition-all hover:bg-muted/40 active:scale-[0.99]"
        data-testid={`paradero-fisico-${paradero.id}`}
      >
        <MapPin className="h-6 w-6 shrink-0 text-primary" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block font-headline text-base font-bold leading-snug text-foreground">
            {paradero.nombre}
          </span>
          <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {paradero.recorridos.map((r) => (
              <span
                key={r.id}
                className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
                style={{ backgroundColor: getLandingCardColors(r).background }}
              >
                {r.codigo}
              </span>
            ))}
          </span>
        </span>
        {paradero.distanciaM != null && (
          <span className="shrink-0 text-sm font-bold tabular-nums text-muted-foreground">
            {formatoDistancia(paradero.distanciaM)}
          </span>
        )}
        <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
      </button>
    </li>
  );
}

export default function CercaView() {
  const navigate = useNavigate();
  const [ubicacion, setUbicacion] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [geoError, setGeoError] = useState(null);
  const [texto, setTexto] = useState('');

  useDocumentTitle('Paraderos cerca de mí');

  const pedirUbicacion = useCallback(() => {
    if (!geolocationDisponible()) {
      setGeoError('La ubicación no está disponible en este dispositivo o navegador.');
      trackBuscarCercanos('no_disponible');
      return;
    }
    setBuscando(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUbicacion({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setBuscando(false);
        trackBuscarCercanos('ok');
      },
      (err) => {
        setGeoError(geolocationErrorMessage(err));
        setBuscando(false);
        trackBuscarCercanos(err?.code === err?.PERMISSION_DENIED ? 'denegado' : 'error');
      },
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 }
    );
  }, []);

  /** La persona llegó aquí tocando «cerca de mí»: se pide la ubicación de inmediato. */
  useEffect(() => {
    pedirUbicacion();
  }, [pedirUbicacion]);

  const cercanos = useMemo(
    () => (ubicacion ? paraderosMasCercanos(ubicacion.lat, ubicacion.lng, 5) : []),
    [ubicacion]
  );
  /** Varios paraderos comparten nombre: con ubicación, la distancia ayuda a distinguirlos. */
  const encontrados = useMemo(
    () =>
      buscarParaderos(texto).map((p) =>
        ubicacion
          ? { ...p, distanciaM: distanciaM(ubicacion.lat, ubicacion.lng, p.lat, p.lng) }
          : p
      ),
    [texto, ubicacion]
  );
  const lejos = cercanos.length > 0 && cercanos[0].distanciaM > LEJOS_DE_LA_RED_M;

  const abrir = (origen) => (paradero) => {
    trackVerLlegadas({ paraderoFisicoId: paradero.id, origen });
    navigate(llegadasPath({ paraderoFisicoId: paradero.id }));
  };

  return (
    <div className="min-h-screen bg-background px-4 pb-32 pt-8" data-testid="cerca-view">
      <div className="mx-auto max-w-md space-y-6">
        <header className="flex items-center gap-3">
          <Link to="/" aria-label="Ir al inicio" className="shrink-0 rounded-lg">
            <img
              src={`${process.env.PUBLIC_URL || ''}/img/Logo-MuniBus.png`}
              alt=""
              className="h-10 w-auto"
            />
          </Link>
          <h1 className="font-headline text-2xl font-extrabold tracking-tight text-foreground">
            Paraderos cerca de mí
          </h1>
        </header>

        <section aria-label="Paraderos más cercanos" className="space-y-3">
          {buscando && (
            <div className="space-y-3" aria-busy="true" aria-label="Buscando tu ubicación">
              <div className="skeleton h-20 w-full rounded-2xl" />
              <div className="skeleton h-20 w-full rounded-2xl" />
              <div className="skeleton h-20 w-full rounded-2xl" />
            </div>
          )}

          {!buscando && geoError && (
            <div
              className="rounded-2xl border border-border/60 bg-muted/30 p-4 text-sm text-foreground"
              role="status"
              data-testid="cerca-geo-error"
            >
              <p className="font-semibold">{geoError}</p>
              <p className="mt-1 text-muted-foreground">
                También puedes buscar tu paradero por nombre más abajo.
              </p>
            </div>
          )}

          {!buscando && lejos && (
            <p
              className="rounded-2xl border border-border/60 bg-muted/30 p-4 text-sm text-muted-foreground"
              data-testid="cerca-lejos"
            >
              Estás a {formatoDistancia(cercanos[0].distanciaM)} del paradero más cercano. Estos son
              los más próximos a tu ubicación.
            </p>
          )}

          {!buscando && cercanos.length > 0 && (
            <ul className="space-y-3" data-testid="cerca-lista">
              {cercanos.map((p) => (
                <ParaderoItem key={p.id} paradero={p} onSelect={abrir('cercania')} />
              ))}
            </ul>
          )}

          {!buscando && (
            <Button
              type="button"
              variant="outline"
              onClick={pedirUbicacion}
              className="touch-target w-full"
              data-testid="cerca-usar-ubicacion"
            >
              <LocateFixed className="mr-2 h-4 w-4" aria-hidden />
              {ubicacion ? 'Actualizar mi ubicación' : 'Usar mi ubicación'}
            </Button>
          )}

          <p className="text-xs leading-relaxed text-muted-foreground">
            Tu ubicación se usa solo en tu teléfono para ordenar los paraderos; no se envía ni se
            guarda.
          </p>
        </section>

        <section aria-label="Buscar paradero por nombre" className="space-y-3">
          <label
            htmlFor="buscar-paradero"
            className="text-[11px] font-extrabold uppercase tracking-widest text-muted-foreground"
          >
            Buscar por nombre
          </label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              id="buscar-paradero"
              type="search"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Ej.: Estación Efe Paine"
              className="pl-9"
              autoComplete="off"
              data-testid="cerca-buscar"
            />
          </div>
          {texto.trim().length >= 2 && encontrados.length === 0 && (
            <p className="text-sm text-muted-foreground">No hay paraderos con ese nombre.</p>
          )}
          {encontrados.length > 0 && (
            <ul className="space-y-3" data-testid="cerca-resultados">
              {encontrados.map((p) => (
                <ParaderoItem key={p.id} paradero={p} onSelect={abrir('busqueda')} />
              ))}
            </ul>
          )}
        </section>
      </div>

      <BottomNav onRefresh={pedirUbicacion} refreshing={buscando} />
    </div>
  );
}
